import axios from 'axios';
import { Auth, LoginService, Message } from '../api';
import store from '../redux/store';
import { ActionTypes } from '../redux/actions/types';
import { setNotification } from '../utils/notificationUtils';
import { ERROR } from '../layouts/notification/Notification';

let isRefreshing = false;
let failedQueue: Array<any> = [];

export const clearFailedRequestQueue = () => {
  failedQueue = [];
};

export async function handle401(err: any): Promise<any> {
  const originalRequest = err.config;

  if (shouldLogout(err.response.data)) {
    logout(err.response.data);
  } else if (!originalRequest._retry) {
    return await handleRefreshToken(originalRequest);
  } else {
    setNotification('Unbekannter Fehler aufgetreten. Bitte neu anmeleden.', []);
    logout();
  }
}

/**
 * Dient um den Refresh Token zu erneuern wenn eine 401 vom Server kommt.
 * Wenn ein Refresh Token Request schon abgefeuert wurde, wird die fehlerhafte Request zu einem
 * Queue hinzugefügt bis die Refresh Token Request mit den neuen Access Token zurück ist.
 * Mit den neuen Access Token werden dann alle Failed Requests wiederholt.
 *
 * @param originalRequest die 401 Response einer originalen Request (wobei der Access Token nicht akzeptiert wurde)
 */
export async function handleRefreshToken(originalRequest: any): Promise<any> {
  // 1. Wenn der Refresh Token request schon lauft, wird die Request zu eine failedQueue gepushed
  if (isRefreshing) {
    return new Promise(function (resolve, reject) {
      failedQueue.push({
        resolve,
        reject,
      });
    })
      .then(token => {
        // 2. um die spaeter zu wiederholen mit den neuen Access Token
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return axios(originalRequest);
      })
      .catch(err => {
        clearAuthState();
        return Promise.reject(err);
      });
  }

  // Wenn der Refresh Token Request noch nicht abgefeuert wurde, wird er im folgenden
  // Promise abgefeuert und mit der Antwort werden durch der processQueue() methode alle
  // folgende Fehlerhafte Requests erneut abgeschickt.
  originalRequest._retry = true;
  isRefreshing = true;
  return new Promise(function (resolve, reject) {
    new LoginService()
      .refreshTokens({
        refreshToken: localStorage.getItem('refreshtoken') ?? '',
        accessToken: localStorage.getItem('token') ?? '',
      })
      .then(refreshTokenResponse => {
        if (refreshTokenResponse) {
          const data = refreshTokenResponse.data;
          axios.defaults.headers.common['Authorization'] =
            'Bearer ' + data.accessToken;
          originalRequest.headers['Authorization'] =
            'Bearer ' + data.accessToken;
          processQueue(null, data.accessToken);
          setAuthState(data);
          resolve(axios(originalRequest));
        } else {
          logout();
        }
      })
      .catch(err => {
        processQueue(err, null);
        clearAuthState();

        reject(err);
      })
      .then(() => {
        isRefreshing = false;
      });
  });
}

/**
 * Wenn ein Token vorhanden ist, wiederholt eine Fehlerhaften Request mit den neuen Token.
 *
 * @param error die ursprüngliche error Response
 * @param token der neue Token
 */
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/*
Leert den Auth State und zeigt an eine Fehlermeldung.
 */
const logout = (messages?: Message[]) => {
  //Ab zur Login Seite!
  store.dispatch({
    type: ActionTypes.SET_AUTHENTICATION,
    payload: {},
  });
  if (messages) {
    store.dispatch({
      type: ActionTypes.ADD_NOTIFICATIONS,
      payload: messages,
    });
  } else {
    setNotification(
      'Anmeldung abgelaufen. Bitte melden Sie sich erneut an.',
      [],
      ERROR,
      null
    );
  }
};

/**
 * Setzt den Auth State im Redux
 * @param data die neue Tokens als objekt
 */
const setAuthState = (data: any) => {
  store.dispatch({
    type: ActionTypes.SET_AUTHENTICATION,
    payload: data,
  });
};

/**
 * Leert den Auth State in Redux.
 */
const clearAuthState = () => {
  return setAuthState({});
};

/**
 * Bedingung ob der client zur login seite gehen soll.
 *
 * @param errorDtoList die Liste von Fehlermeldungen
 */
export const shouldLogout = (errorDtoList: Message[]) => {
  // TODO: eine bessere Loesung finden als den Text der Meldung zu vergleichen.
  return (
    errorDtoList &&
    errorDtoList[0]?.message === 'Bitte melden Sie sich erneut an.'
  );
};
